import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Search, Filter, AlertCircle, Repeat, CheckCircle, XCircle, Plus, UserPlus, CalendarDays, FileText, Clock } from "lucide-react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import ClientSelector from "@/components/admin/ClientSelector";
import ScheduleAppointmentDialog from "@/components/client/ScheduleAppointmentDialog";
import SendIntakeFormDialog from "@/components/admin/SendIntakeFormDialog";
import { TIMEZONE_LABEL } from "@/lib/timezoneConfig";

interface Appointment {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  service_type: string;
  appointment_type: string | null;
  preferred_date: string | null;
  preferred_time: string | null;
  preferred_timezone: string | null;
  notification_preference: string | null;
  is_recurring: boolean | null;
  recurrence_pattern: any | null;
  parent_appointment_id: string | null;
  modification_request: any | null;
  modification_status: string | null;
  requires_approval: boolean | null;
  approval_reason: string | null;
  status: string;
  created_at: string;
}

// Helper function to calculate timestamps from date/time strings
const calculateTimestamps = (dateStr: string, timeStr: string) => {
  const [time, period] = timeStr.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  
  const [year, month, day] = dateStr.split('-').map(Number);
  const scheduledStart = new Date(year, month - 1, day, hours, minutes, 0, 0);
  const scheduledEnd = new Date(scheduledStart.getTime() + 60 * 60000); // 60 min default
  
  return {
    scheduled_start_time: scheduledStart.toISOString(),
    scheduled_end_time: scheduledEnd.toISOString(),
  };
};

export default function Scheduling() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [newClientData, setNewClientData] = useState({ full_name: "", email: "", phone: "" });
  const [creatingClient, setCreatingClient] = useState(false);
  const [showSendIntakeDialog, setShowSendIntakeDialog] = useState(false);
  const [selectedAppointmentForIntake, setSelectedAppointmentForIntake] = useState<any>(null);
  const { toast } = useToast();

  const pendingApprovalAppointments = appointments.filter(
    apt => apt.status === 'pending_approval' || (apt.status === 'pending' && apt.requires_approval)
  );

  useEffect(() => {
    fetchAppointments();

    const channel = supabase
      .channel("appointments-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "appointments" },
        () => {
          fetchAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchAppointments = async () => {
    try {
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .neq('status', 'cancelled')
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast({
        title: "Error",
        description: "Failed to load appointments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      // Fetch appointment to check if we need to calculate timestamps
      const { data: appointment, error: fetchError } = await supabase
        .from('appointments')
        .select('preferred_date, preferred_time, scheduled_start_time, scheduled_end_time')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      let updateData: any = { status: newStatus };

      // If confirming and timestamps are missing, calculate them
      if (newStatus === 'confirmed' && !appointment.scheduled_start_time && appointment.preferred_date && appointment.preferred_time) {
        const timestamps = calculateTimestamps(appointment.preferred_date, appointment.preferred_time);
        updateData = { ...updateData, ...timestamps };
      }

      const { error } = await supabase
        .from("appointments")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Appointment status updated",
      });
      fetchAppointments();
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const filteredAppointments = appointments.filter((apt) => {
    const matchesSearch =
      apt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (apt.phone && apt.phone.includes(searchTerm));
    const matchesStatus = statusFilter === "all" || apt.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const approveModification = async (appointment: Appointment) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          preferred_date: appointment.modification_request.requested_date,
          preferred_time: appointment.modification_request.requested_time,
          modification_status: 'approved',
          modification_request: null,
        })
        .eq('id', appointment.id);

      if (error) throw error;

      toast({
        title: 'Modification Approved',
        description: 'Appointment has been updated with the new date/time',
      });
    } catch (error) {
      console.error('Error approving modification:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve modification',
        variant: 'destructive',
      });
    }
  };

  const denyModification = async (appointment: Appointment) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          modification_status: 'denied',
          modification_request: null,
        })
        .eq('id', appointment.id);

      if (error) throw error;

      toast({
        title: 'Modification Denied',
        description: 'Client will keep their original appointment time',
      });
    } catch (error) {
      console.error('Error denying modification:', error);
      toast({
        title: 'Error',
        description: 'Failed to deny modification',
        variant: 'destructive',
      });
    }
  };

  const approveAfterHours = async (appointmentId: string) => {
    try {
      // First, get the appointment to check if we need to calculate timestamps
      const { data: appointment, error: fetchError } = await supabase
        .from('appointments')
        .select('preferred_date, preferred_time, scheduled_start_time, scheduled_end_time')
        .eq('id', appointmentId)
        .single();

      if (fetchError) throw fetchError;

      let updateData: any = {
        status: 'confirmed',
        requires_approval: false,
      };

      // If timestamps are missing, calculate them
      if (!appointment.scheduled_start_time && appointment.preferred_date && appointment.preferred_time) {
        const timestamps = calculateTimestamps(appointment.preferred_date, appointment.preferred_time);
        updateData = { ...updateData, ...timestamps };
      }

      const { error } = await supabase
        .from('appointments')
        .update(updateData)
        .eq('id', appointmentId);

      if (error) throw error;

      toast({
        title: 'After-Hours Appointment Approved',
        description: 'Client will be notified of the confirmation',
      });
    } catch (error) {
      console.error('Error approving after-hours:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve appointment',
        variant: 'destructive',
      });
    }
  };

  const denyAfterHours = async (appointmentId: string) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: 'cancelled' })
        .eq('id', appointmentId);

      if (error) throw error;

      toast({
        title: 'After-Hours Request Denied',
        description: 'Client will be notified to choose a different time',
      });
    } catch (error) {
      console.error('Error denying after-hours:', error);
      toast({
        title: 'Error',
        description: 'Failed to deny appointment',
        variant: 'destructive',
      });
    }
  };

  const handleCreateQuickClient = async () => {
    if (!newClientData.full_name || !newClientData.email) {
      toast({
        title: "Required Fields",
        description: "Please provide client name and email",
        variant: "destructive",
      });
      return;
    }

    setCreatingClient(true);
    try {
      const { data, error } = await supabase
        .from("clients")
        .insert([{
          full_name: newClientData.full_name,
          email: newClientData.email,
          phone: newClientData.phone || null,
          client_status: 'active',
        }])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Client Created",
        description: "New client added successfully",
      });

      setSelectedClient(data);
      setShowNewClientForm(false);
      setShowClientSelector(false);
      setShowAppointmentDialog(true);
      setNewClientData({ full_name: "", email: "", phone: "" });
    } catch (error) {
      console.error("Error creating client:", error);
      toast({
        title: "Error",
        description: "Failed to create client",
        variant: "destructive",
      });
    } finally {
      setCreatingClient(false);
    }
  };

  const getStatusBadge = (status: string, requiresApproval?: boolean) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      pending_approval: "secondary",
      confirmed: "default",
      completed: "outline",
      cancelled: "destructive",
    };
    const labels: Record<string, string> = {
      pending_approval: "Pending Approval",
      pending: requiresApproval ? "Pending Approval" : "Pending",
      confirmed: "Confirmed",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return <Badge variant={variants[status] || "outline"}>{labels[status] || status}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Calendar className="h-8 w-8" />
              Scheduling
            </h1>
            <p className="text-muted-foreground mt-2 flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Manage client appointments and scheduling • All times shown in {TIMEZONE_LABEL}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/scheduling/calendar')} variant="outline" size="lg">
              <CalendarDays className="h-5 w-5 mr-2" />
              Calendar View
            </Button>
            <Button onClick={() => setShowClientSelector(true)} size="lg">
              <Plus className="h-5 w-5 mr-2" />
              New Appointment
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Appointments</CardTitle>
            <CardDescription>
              View and manage all client appointments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {pendingApprovalAppointments.length > 0 && (
              <div className="mb-6 space-y-3">
                <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 font-semibold">
                  <AlertCircle className="h-5 w-5" />
                  After-Hours Appointments Pending Approval ({pendingApprovalAppointments.length})
                </div>
                {pendingApprovalAppointments.map((apt) => (
                  <Alert key={apt.id} className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
                    <AlertDescription className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="font-semibold text-base mb-1">{apt.name}</div>
                        <div className="text-sm space-y-1">
                          <div>
                            <span className="text-muted-foreground">Service:</span> {apt.service_type}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Requested Time:</span>{' '}
                            {apt.preferred_date && format(new Date(apt.preferred_date), 'MMM d, yyyy')} at{' '}
                            {apt.preferred_time}
                          </div>
                          <div>
                            <span className="text-muted-foreground">Contact:</span> {apt.email}
                            {apt.phone && ` • ${apt.phone}`}
                          </div>
                          {apt.approval_reason && (
                            <div className="text-orange-700 dark:text-orange-400 font-medium">
                              {apt.approval_reason}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approveAfterHours(apt.id)}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => denyAfterHours(apt.id)}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Deny
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">Loading appointments...</div>
            ) : filteredAppointments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No appointments found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Date/Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAppointments.map((apt) => (
                      <>
                        {apt.modification_status === 'pending' && apt.modification_request && (
                          <TableRow key={`mod-${apt.id}`}>
                            <TableCell colSpan={6}>
                              <Alert className="border-orange-200 bg-orange-50">
                                <AlertCircle className="h-4 w-4 text-orange-600" />
                                <AlertDescription className="flex items-center justify-between">
                                  <div>
                                    <strong>{apt.name}</strong> requested to change to{' '}
                                    {format(new Date(apt.modification_request.requested_date), 'MMM d, yyyy')} at{' '}
                                    {apt.modification_request.requested_time}
                                    {apt.modification_request.reason && (
                                      <div className="text-sm mt-1">
                                        Reason: {apt.modification_request.reason}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-2 ml-4">
                                    <Button size="sm" onClick={() => approveModification(apt)}>
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      Approve
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => denyModification(apt)}>
                                      <XCircle className="h-4 w-4 mr-1" />
                                      Deny
                                    </Button>
                                  </div>
                                </AlertDescription>
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow key={apt.id}>
                        <TableCell className="font-medium">{apt.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{apt.email}</div>
                            {apt.phone && (
                              <div className="text-muted-foreground">{apt.phone}</div>
                            )}
                            {apt.notification_preference && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Notify: {apt.notification_preference}
                              </div>
                            )}
                            {apt.preferred_timezone && apt.appointment_type === 'virtual' && (
                              <div className="text-xs text-muted-foreground">
                                TZ: {apt.preferred_timezone.split('/')[1]}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{apt.service_type}</div>
                            {apt.appointment_type && (
                              <div className="text-muted-foreground">
                                {apt.appointment_type}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {apt.preferred_date ? (
                            <div className="text-sm">
                              <div>{format(new Date(apt.preferred_date), "MMM d, yyyy")}</div>
                              {apt.preferred_time && (
                                <div className="text-muted-foreground">
                                  {apt.preferred_time}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">Not set</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {getStatusBadge(apt.status)}
                            {apt.is_recurring && (
                              <Badge variant="outline" className="gap-1 w-fit">
                                <Repeat className="h-3 w-3" />
                                {apt.parent_appointment_id ? 'Series' : 'Parent'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {apt.status === "pending" && (
                              <Button
                                size="sm"
                                onClick={() => updateStatus(apt.id, "confirmed")}
                              >
                                Confirm
                              </Button>
                            )}
                            {apt.status === "confirmed" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateStatus(apt.id, "completed")}
                                >
                                  Complete
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedAppointmentForIntake(apt);
                                    setShowSendIntakeDialog(true);
                                  }}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  Send Intake
                                </Button>
                              </>
                            )}
                            {apt.status !== "cancelled" && apt.status !== "completed" && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => updateStatus(apt.id, "cancelled")}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Selector Dialog */}
        <Dialog open={showClientSelector} onOpenChange={(open) => {
          setShowClientSelector(open);
          if (!open) {
            setShowNewClientForm(false);
            setNewClientData({ full_name: "", email: "", phone: "" });
          }
        }}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Select Client for Appointment</DialogTitle>
              <DialogDescription>
                Choose an existing client or create a new one
              </DialogDescription>
            </DialogHeader>
            
            {!showNewClientForm ? (
              <div className="space-y-4">
                <ClientSelector 
                  onClientSelect={(id, email, client) => {
                    setSelectedClient(client);
                    setShowClientSelector(false);
                    setShowAppointmentDialog(true);
                  }}
                />
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or
                    </span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setShowNewClientForm(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create New Client
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-client-name">Full Name *</Label>
                  <Input
                    id="new-client-name"
                    placeholder="John Doe"
                    value={newClientData.full_name}
                    onChange={(e) => setNewClientData({ ...newClientData, full_name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-client-email">Email *</Label>
                  <Input
                    id="new-client-email"
                    type="email"
                    placeholder="john@example.com"
                    value={newClientData.email}
                    onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-client-phone">Phone</Label>
                  <Input
                    id="new-client-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={newClientData.phone}
                    onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowNewClientForm(false);
                      setNewClientData({ full_name: "", email: "", phone: "" });
                    }}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleCreateQuickClient}
                    disabled={creatingClient}
                    className="flex-1"
                  >
                    {creatingClient ? "Creating..." : "Create & Schedule"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Appointment Dialog */}
        {selectedClient && (
          <ScheduleAppointmentDialog
            open={showAppointmentDialog}
            onOpenChange={(open) => {
              setShowAppointmentDialog(open);
              if (!open) {
                setSelectedClient(null);
              }
            }}
            clientData={{
              id: selectedClient.id,
              full_name: selectedClient.full_name,
              email: selectedClient.email,
              phone: selectedClient.phone,
            }}
          />
        )}

        {/* Send Intake Form Dialog */}
        <SendIntakeFormDialog
          open={showSendIntakeDialog}
          onOpenChange={setShowSendIntakeDialog}
          appointment={selectedAppointmentForIntake}
          onSent={() => {
            fetchAppointments();
            setSelectedAppointmentForIntake(null);
          }}
        />
      </div>
    </AdminLayout>
  );
}
